import { getDataItemValue } from '../helper/standardChart';
import { deepMerge, toArray, deepRemove } from '../util/data';
import { type StandardChartOption } from './insightManager';
import { type ExtensionApi } from '../core/extension';

const BLUR = 0.2;

interface StyleOptions {
  opacity: number;
}

interface BlurSeriesOptions {
  itemStyle?: StyleOptions;
  lineStyle?: StyleOptions;
  areaStyle?: StyleOptions;
}

export function findExtend(data: (number | { value: number | string } | string)[]) {
  let min = +Infinity;
  let max = -Infinity;

  data.forEach(item => {
    let number;
    if (typeof item === 'object') {
      number = item.value;
    } else {
      number = +item;
    }
    if (Array.isArray(number)) {
      min = Math.min(min, +number[1]);
      max = Math.max(max, +number[1]);
    } else {
      min = Math.min(min, +number);
      max = Math.max(max, +number);
    }
  });

  return [Math.min(min, 0), max];
}

/**
 * Copy from echarts source code: src/util/number.ts
 *
 * Convert a percent string to absolute number.
 * Returns NaN if percent is not a valid string or number
 */
export function parsePercent(percent: number | string, all: number): number {
  switch (percent) {
    case 'center':
    case 'middle':
      percent = '50%';
      break;
    case 'left':
    case 'top':
      percent = '0%';
      break;
    case 'right':
    case 'bottom':
      percent = '100%';
      break;
  }
  if (typeof percent === 'string') {
    if (percent.trim().match(/%$/)) {
      return (parseFloat(percent) / 100) * all;
    }

    return parseFloat(percent);
  }

  return percent == null ? NaN : +percent;
}

/** 从 echarts 的 series 数组中获取每个 series 的 data 数组，组成二维数组 */
export function retrieveTwoDimArray(
  source: {
    data: number | string | { value: number | string }[];
  }[]
) {
  return source.map(item =>
    Array.isArray(item.data) ? item.data.map(getDataItemValue) : [getDataItemValue(item.data)]
  ) as number[][] | string[][];
}

/** 将二维数组按列计算平均值 */
export function calculateColumnAverages(matrix: number[][] | string[][]): number[] {
  // 处理空数组或不规则数组的情况
  if (!matrix || matrix.length === 0) {
    return [];
  }

  // 获取最大列数（处理不规则数组）
  const columnCount = Math.max(...matrix.map(row => row.length));

  // 创建列平均值数组
  return Array.from({ length: columnCount }, (_, colIndex) => {
    // 过滤出当前列存在的值并计算平均值
    const columnValues = matrix.map(row => +row[colIndex]).filter(val => val !== undefined);

    return columnValues.length > 0
      ? columnValues.reduce((sum, val) => sum + val, 0) / columnValues.length
      : 0;
  });
}

/** 计算展开数组 */
export function expandRange(
  arr: number[],
  options: {
    inclusive?: boolean;
    step?: number;
  } = {}
): number[] {
  // 参数校验
  if (!Array.isArray(arr) || arr.length !== 2) {
    throw new Error('Input must be an array with exactly 2 elements');
  }

  const {
    // 默认包含首尾
    inclusive = true,
    // 默认步进为1
    step = 1
  } = options;

  const [start, end] = arr.sort((a, b) => a - b);

  // 处理步进
  const result = [];
  for (let i = start; inclusive ? i <= end : i < end; i += step) {
    result.push(i);
  }

  return result;
}

export function handleLegendBlur<T>(option: StandardChartOption, target: T & { name: string }) {
  const legendItem = option.legend?.data?.find(item => item.name === target.name);
  if (legendItem) {
    (legendItem as { __blur: number }).__blur = BLUR;
  }
}

/** 多系列时，联动元素与legend的弱化效果 */
export function handleMultipleSeries(
  option: StandardChartOption,
  seriesIndex: number,
  legendIns: InstanceType<ExtensionApi['ui']['Legend']['Legend']>,
  blurSeriesOpt: BlurSeriesOptions,
  linkLegend: boolean,
  comparedSeriesIndex?: number
) {
  (option.series as []).forEach((series, index) => {
    if ((series as { __highlight: boolean }).__highlight) {
      return;
    }
    if (comparedSeriesIndex && comparedSeriesIndex === index) {
      deepRemove(series, blurSeriesOpt);
      deepRemove(option.legend?.data?.find(item => item.name === series.name), { __blur: BLUR });
      (series as { __highlight: boolean }).__highlight = true;
      return;
    }

    if (seriesIndex === index) {
      deepRemove(series, blurSeriesOpt);
      deepRemove(option.legend?.data?.find(item => item.name === series.name), { __blur: BLUR });
      (series as { __highlight: boolean }).__highlight = true;
      return;
    }

    // Update legend blur effect
    if (linkLegend) {
      handleLegendBlur(option, series);
    }

    // Update series with blur options
    option.series[index] = deepMerge(blurSeriesOpt, series, { override: true });
  });

  // Update legend instance if available
  if (legendIns && option.legend?.data && linkLegend) {
    legendIns.update(option.legend.data);
  }
}

export function handleAnimation<T>(
  seriesMark: T & { animationDuration?: number },
  animationDuration: number,
  markerSeries?: T & {
    dvAnimationConfig: unknown;
  }
) {
  if (!animationDuration) {
    return;
  }
  const delay = animationDuration;
  const chartSeries = seriesMark;
  const dvMarkerSeries = markerSeries;
  chartSeries.animationDuration = delay;

  if (dvMarkerSeries) {
    dvMarkerSeries.dvAnimationConfig = {
      enterAnimation: {
        delay,
        duration: delay
      },
      enterFrom: {
        style: {
          opacity: 0
        }
      }
    };
  }
}

export function isMultipleSeriesWithoutDvMarker(option: StandardChartOption) {
  return toArray(option.series).filter(item => item.type !== 'dvMarker').length > 1;
}
